/**
 * What the owner put in and took out, beside what the strategy earned.
 *
 * Every other figure on this product has owner movements removed — that is the
 * rule that stops a deposit reading as profit. This block is where they are
 * shown, and it exists for the same reason: a month that gained five hundred
 * after a thousand went in is not a good month, and a reader who cannot see
 * the thousand has no way to know that.
 *
 * Direction comes from the sign as recorded, never from a second field. Two
 * fields describing one fact are two things that can disagree, and the day
 * they do the screen says a withdrawal was a deposit.
 */
import { ArrowDownToLine, ArrowUpFromLine } from "lucide-react";

import type { CashFlowResponse } from "@/shared/api";
import { isNegative, signedMoney, toPlaces } from "@/shared/money/money";
import { PANEL_HEADER, PANEL_TITLE } from "@/design-system/Panel";

function moment(at: string): string {
  return new Date(at).toLocaleString("ru-RU", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function CashFlows({
  flows,
  loading,
}: {
  readonly flows: readonly CashFlowResponse[];
  readonly loading: boolean;
}): React.JSX.Element {
  // Newest first: the last movement is the one a reader is checking against.
  const shown = [...flows].reverse();

  return (
    <section
      data-testid="cash-flows"
      aria-labelledby="cash-flows-heading"
      className="flex min-w-0 flex-col overflow-hidden rounded-panel border border-border-subtle bg-surface-default"
    >
      <div className={`${PANEL_HEADER} justify-between gap-3`}>
        <h2 id="cash-flows-heading" className={PANEL_TITLE}>
          Пополнения и выводы
        </h2>
        <span className="text-helper-sm text-text-muted" data-testid="cash-flows-count">
          {`${String(flows.length)} за период`}
        </span>
      </div>

      {loading ? (
        <p className="px-3 py-3 text-helper-sm text-text-muted">Читаю движения…</p>
      ) : shown.length === 0 ? (
        <p className="px-3 py-3 text-helper-sm text-text-muted" data-testid="cash-flows-empty">
          За период не было ни пополнений, ни выводов
        </p>
      ) : (
        <ul className="divide-y divide-border-subtle">
          {shown.map((flow) => {
            const out = isNegative(toPlaces(flow.amount));
            return (
              <li
                key={`${flow.at}-${flow.amount}`}
                data-testid={`flow-${flow.at}`}
                data-direction={out ? "out" : "in"}
                className="flex min-w-0 items-center gap-3 px-3 py-2"
              >
                {/* The glyph carries the direction as well as the colour and
                    the sign: one man in twelve cannot use the colour. */}
                {out ? (
                  <ArrowUpFromLine
                    aria-hidden="true"
                    className="size-4 shrink-0 text-status-warning"
                  />
                ) : (
                  <ArrowDownToLine
                    aria-hidden="true"
                    className="size-4 shrink-0 text-status-info"
                  />
                )}
                <span className="min-w-0 flex-1">
                  <span className="block text-body-main text-text-primary">
                    {out ? "Вывод" : "Пополнение"}
                  </span>
                  <time
                    dateTime={flow.at}
                    className="block text-helper-sm tabular-nums text-text-muted"
                  >
                    {moment(flow.at)}
                  </time>
                </span>
                <span
                  className={`flex shrink-0 items-baseline gap-1 text-body-main tabular-nums ${
                    out ? "text-status-warning" : "text-text-primary"
                  }`}
                >
                  {signedMoney(flow.amount)}
                  <span className="text-helper-sm font-normal text-text-muted">USDT</span>
                </span>
              </li>
            );
          })}
        </ul>
      )}

      {/* Said out loud, because it is the rule every other figure depends on. */}
      <p className="border-t border-border-subtle px-3 py-2 text-helper-sm text-text-muted">
        Эти движения исключены из всех показателей доходности выше.
      </p>
    </section>
  );
}
